import React, { useState, useEffect } from 'react';
import usePageTitle from '../../hooks/usePageTitle';
import { ArrowRight, Instagram } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import MenuMobilePublic from './MenuMobilePublic';
import { KaneaLogo } from '../../components/Common/KaneaLogo';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import LandingPricingSection from './LandingPricingSection';
import InstagramFeed from './InstagramFeed';
import { INSTAGRAM_URL } from './socialLinks';
import './LandingPage.css';
import productDashboard from '../../assets/landing/product_dashboard.png';
import productValuation from '../../assets/landing/product_valuation_snts.png';
import productNia from '../../assets/landing/product_nia_copilot.png';


// ─── Interfaces ──────────────────────────────────────────────────────────────
interface StockPrice { date: string; close: number; }
interface SntsData {
  symbol: string;
  name: string;
  sector: string;
  last_close: number | null;
  variation_pct: number | null;
  rsi_14: number | null;
  perf_30d_pct: number | null;
  last_date: string | null;
  prices: StockPrice[];
}


// ─── Feature Cards ───────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: '⚡',
    title: 'Flash Marché IA',
    desc: 'Résumé synthétique quotidien du marché. Découvrez les plus fortes variations et les tendances du jour générées par notre IA.',
    tag: 'Gratuit',
    isPro: false,
  },
  {
    icon: '💎',
    title: 'Indice de Valeur Kanea',
    desc: 'L\'estimation fondamentale des actions. Notre moteur croise 14 modèles de valorisation pour estimer le potentiel théorique de chaque titre.',
    tag: 'Pro',
    isPro: true,
  },
  {
    icon: '🤖',
    title: 'Nia, votre copilote BRVM',
    desc: 'Posez vos questions en français simple à Nia. Elle parcourt instantanément 10 ans d\'historique, déchiffre les bilans comptables officiels et vous répond sans jargon — disponible 24h/24.',
    tag: 'Pro',
    isPro: true,
  },
  {
    icon: '🔔',
    title: 'Alertes techniques',
    desc: 'Restez informé des mouvements du marché. Alertes automatisées sur les croisements RSI, bandes de Bollinger, et autres indicateurs de retournement.',
    tag: 'Pro',
    isPro: true,
  },
];

const COMPANIES = [
  'SNTS', 'ORAC', 'SGBC', 'ETIT', 'BOAB', 'ONTBF', 'ECOC',
  'PALM', 'SIBC', 'NTLC', 'TTLC', 'SLBC', 'CFAC', 'CBIBF',
];

// ─── Main Component ──────────────────────────────────────────────────────────
const LandingPage: React.FC = () => {
  usePageTitle("KaneaBourse — Analyse de la BRVM");
  const { user } = useAuth();
  const navigate = useNavigate();
  // Données réelles SNTS
  const [sntsData, setSntsData] = useState<SntsData | null>(null);
  const [sntsLoading, setSntsLoading] = useState(true);

  // Si connecté → dashboard
  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  // Fetch cours SNTS public
  useEffect(() => {
    const fetchSnts = async () => {
      try {
        const res = await fetch(`${API_URL}/public/stock/SNTS`);
        if (!res.ok) throw new Error('API error');
        const json = await res.json();
        setSntsData(json.data);
      } catch {
        setSntsData(null);
      } finally {
        setSntsLoading(false);
      }
    };
    fetchSnts();
  }, []);

  // Formatage du prix
  const formatPrice = (p: number | null) =>
    p ? p.toLocaleString('fr-FR') + ' XOF' : '— XOF';

  return (
    <div className="landing-root">

      {/* ── NAV ── */}
      <nav className="landing-nav">
        <Link to="/home" className="landing-nav-logo">
          <KaneaLogo variant="horizontal" title="KaneaBourse" markColor="#6C3FC4" textColor="var(--lp-text)" height={28} />
          <div className="landing-nav-logo-dot" />
        </Link>

        <ul className="landing-nav-links">
          <li><a href="#features">Fonctionnalités</a></li>
          <li><a href="#tarifs">Tarifs</a></li>
          <li><Link to="/outils">Outils gratuits</Link></li>
          <li><Link to="/blog">Publications</Link></li>
          <li><Link to="/help">Centre d'aide</Link></li>
        </ul>

        <div className="landing-nav-actions">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-nav-social"
            aria-label="KaneaBourse sur Instagram"
            title="Suivre @kaneabourse sur Instagram"
          >
            <Instagram size={18} />
          </a>
          <Link to="/login" className="btn-nav-login">Se connecter</Link>
          {/* « Compte gratuit » et non « Essai gratuit » : le plan gratuit est permanent,
              or « essai » fait craindre une bascule payante et une carte à renseigner. */}
          <Link to="/members/signup" className="btn-nav-signup">Compte gratuit</Link>
          <MenuMobilePublic liens={[{ label: "Fonctionnalités", to: '#features' }, { label: "Tarifs", to: '#tarifs' }, { label: "Outils gratuits", to: '/outils' }, { label: "Publications", to: '/blog' }, { label: "Centre d'aide", to: '/help' }]} />
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-hero-badge animate-in delay-1">
            <span />
            Données officielles BRVM · Mis à jour chaque soir après clôture
          </div>

          <h1 className="animate-in delay-1">
            N'investissez plus<br />
            sur des <em>rumeurs</em>
          </h1>

          <p className="landing-hero-sub animate-in delay-2">
            KaneaBourse analyse froidement les bilans officiels de chaque société cotée à la BRVM.
            14 modèles de valorisation. Indicateurs techniques. Nia, votre copilote financier en français.
            Analysez avec KaneaBourse, passez vos ordres chez votre SGI.
          </p>

          <div className="landing-hero-cta animate-in delay-3">
            <Link to="/members/signup" className="btn-primary-lg">
              Créer un compte gratuit →
            </Link>
            <a href="#demo" className="btn-outline-lg">
              Voir la démo
            </a>
          </div>

          <p className="landing-hero-reassurance animate-in delay-3">
            Gratuit, sans limite de durée · Sans carte bancaire · Abonnement payable en Mobile Money
          </p>

          {/* ── Mockup Dashboard Réel ── */}
          <div className="landing-hero-mockup-wrapper animate-in delay-3">
            <div className="landing-hero-mockup-frame">
              <div className="landing-mockup-header">
                <div className="landing-mockup-dots">
                  <span className="dot red" />
                  <span className="dot yellow" />
                  <span className="dot green" />
                </div>
                <div className="landing-mockup-url">kaneabourse.com/dashboard</div>
              </div>
              <img
                src={productDashboard}
                alt="Tableau de bord KaneaBourse - Terminal d'analyse de la BRVM"
                className="landing-mockup-img"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ──
          Trois bénéfices côté visiteur, et non trois métriques côté produit.
          Écartés volontairement :
          - « 48 valeurs » : c'est le marché entier, tout le monde les a. La couverture
            reste affirmée plus bas, dans « 48 sociétés analysées en continu ».
          - « 10 ans de données » : ouvrait un axe de comparaison où la concurrence
            annonce 20 ans, alors que la profondeur n'est pas le sujet en valorisation
            fondamentale.
          - « 14 modèles » : métrique de producteur, que le visiteur ne peut pas juger
            (aucune échelle de référence) et qu'un concurrent copie en éditant sa page.
            Le chiffre est une preuve, pas une promesse : il reste une seule fois, dans
            la section IVK, où il est nommé et vérifiable (DCF, Gordon-Shapiro, Graham).
          « 10 secondes » reprend la promesse déjà faite dans la section problème. */}
      <div className="landing-stats">
        {[
          { value: '10 s', label: 'pour situer un cours' },
          { value: '48', label: 'sociétés cotées suivies' },
          { value: '24h/24', label: 'avec Nia, copilote IA' },
        ].map(s => (
          <div key={s.label} className="landing-stat">
            <div className="landing-stat-value">{s.value}</div>
            <div className="landing-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── PROBLEM SECTION ── */}
      <section className="landing-problem">
        <div className="problem-content">
          <h2>Les groupes WhatsApp et Telegram disent d'acheter SONATEL à {sntsLoading ? '...' : (sntsData?.last_close?.toLocaleString('fr-FR') ?? '28 800')} FCFA. Mais est-ce réellement un bon prix ? Déterminez-le en 10 secondes, chiffres officiels à l'appui.</h2>
        </div>
      </section>

      {/* ── SOLUTION SECTION (IVK) ── */}
      <section className="landing-solution" id="demo">
        <div className="solution-container">
          <div className="solution-text">
            <div className="landing-section-label">Indice de Valeur Kanea</div>
            <h2 className="landing-section-title" style={{ textAlign: 'left' }}>La juste valeur théorique, estimée objectivement.</h2>
            <p className="landing-section-sub" style={{ textAlign: 'left', marginLeft: 0, marginBottom: 24 }}>
              Visualisez le potentiel de chaque action grâce à notre algorithme financier croisant 14 modèles de valorisation reconnus (DCF, Gordon-Shapiro, Graham, etc.). Un outil d'aide à la décision incontournable avant de passer vos ordres chez votre SGI.
            </p>
          </div>

          <div className="solution-showcase-wrapper">
            <div className="landing-hero-mockup-frame">
              <div className="landing-mockup-header">
                <div className="landing-mockup-dots">
                  <span className="dot red" />
                  <span className="dot yellow" />
                  <span className="dot green" />
                </div>
                <div className="landing-mockup-url">kaneabourse.com/data?symbol=SNTS</div>
              </div>
              <img
                src={productValuation}
                alt="Modèle de Valorisation IVK Sonatel - 14 Modèles"
                className="landing-mockup-img"
              />
            </div>
          </div>
        </div>
      </section>


      {/* ── FEATURES ── */}
      <section className="landing-features" id="features">
        <div className="landing-section-label">Fonctionnalités</div>
        <h2 className="landing-section-title">
          Tout ce dont vous avez besoin<br />pour analyser la BRVM
        </h2>
        <p className="landing-section-sub">
          Des outils professionnels conçus pour les investisseurs individuels et institutionnels de la zone UEMOA.
        </p>

        <div className="features-grid">
          {FEATURES.map(f => (
            <div key={f.title} className="feature-card">
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
              <span className={`feature-tag${f.isPro ? ' pro' : ''}`}>
                {f.tag}
              </span>
            </div>
          ))}
        </div>
      </section>


      {/* ── NIA SPOTLIGHT ── */}
      <section className="landing-nia-spotlight">
        <div className="nia-spotlight-container">
          <div className="nia-spotlight-text">
            <div className="landing-section-label">Copilote Conversationnel IA</div>
            <h2 className="landing-section-title" style={{ textAlign: 'left' }}>
              Nia ne se contente pas de parler.<br />Elle modélise et trace vos graphiques.
            </h2>
            <p className="landing-section-sub" style={{ textAlign: 'left', marginLeft: 0, marginBottom: 20 }}>
              Posez vos questions en français simple : comparaison de performance entre actions, historique de dividendes sur 10 ans, analyse des bilans officiels. Nia extrait les données certifiées BRVM et génère instantanément vos visualisations interactives.
            </p>
            <div className="nia-sample-prompt">
              <span className="prompt-label">Exemple de requête en direct :</span>
              <p className="prompt-text">« Compare Sonatel et Orange CI au BRVM Composite sur 6 mois »</p>
            </div>
            <Link to="/members/signup" className="btn-primary-lg" style={{ marginTop: 24, display: 'inline-flex' }}>
              Discuter avec Nia gratuitement →
            </Link>
          </div>
          <div className="nia-spotlight-visual">
            <div className="landing-hero-mockup-frame">
              <div className="landing-mockup-header">
                <div className="landing-mockup-dots">
                  <span className="dot red" />
                  <span className="dot yellow" />
                  <span className="dot green" />
                </div>
                <div className="landing-mockup-url">kaneabourse.com/chat</div>
              </div>
              <img
                src={productNia}
                alt="Nia Copilote IA avec Graphique Comparatif en Direct"
                className="landing-mockup-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── SOCIAL PROOF — COMPANIES ── */}
      <section className="landing-social-proof" id="marche">
        <p className="companies-label">48 sociétés cotées analysées en continu</p>
        <div className="companies-ticker">
          {COMPANIES.map(c => (
            <div key={c} className="company-chip">{c}</div>
          ))}
          <div className="company-chip" style={{ color: '#334155', borderStyle: 'dashed' }}>+34 autres</div>
        </div>
      </section>

      {/* ── OUTILS GRATUITS TEASER ── */}
      <section style={{ padding: '72px 20px', background: 'var(--lp-bg-2)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <div className="landing-section-label" style={{ marginBottom: 12 }}>Outils gratuits</div>
          <h2 className="landing-section-title">Calculateurs pour investisseurs</h2>
          <p className="landing-section-sub">
            Des outils concrets pour simuler vos gains, comparer les rendements et estimer les frais réels BRVM.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', margin: '40px 0 36px' }}>
            {[
              { emoji: '📈', title: 'Simulateur de gain', desc: 'Plus-value + dividendes + rendement annualisé' },
              { emoji: '💰', title: 'Rendement dividende', desc: 'Yield % vs épargne, obligations, marché BRVM' },
              { emoji: '🧾', title: 'Frais de transaction', desc: 'Coût réel : commission SGI + TVA + AMF-UMOA' },
            ].map(tool => (
              <div key={tool.title} style={{
                flex: '1 1 220px', maxWidth: 260,
                background: 'var(--lp-bg)', border: '1px solid var(--lp-border)',
                borderRadius: 16, padding: '24px 20px', textAlign: 'left',
              }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>{tool.emoji}</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--lp-heading)', marginBottom: 6 }}>{tool.title}</div>
                <div style={{ fontSize: 12, color: 'var(--lp-text-muted)', lineHeight: 1.5 }}>{tool.desc}</div>
              </div>
            ))}
          </div>
          <Link to="/outils" className="btn-outline-lg" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            Accéder aux calculateurs →
          </Link>
        </div>
      </section>

      {/* ── INSTAGRAM ──
          Placé entre les outils gratuits et les tarifs : c'est la sortie de
          secours du visiteur qui n'est pas prêt à payer — plutôt que de le
          perdre, on le convertit en abonné qu'on touchera chaque soir. */}
      <InstagramFeed />

      {/* ── PRICING (Offre Freemium : Gratuit vs Pro) ── */}
      <LandingPricingSection />

      {/* ── FINAL CTA ── */}
      <section className="kanea-purple-banner">
        <div className="kanea-purple-banner__inner">
          <h2 className="kanea-purple-banner__title">Prenez une longueur d'avance<br />sur le marché de la BRVM</h2>
          <p className="kanea-purple-banner__sub">Rejoignez les investisseurs et analystes qui utilisent l'IA de KaneaBourse pour éliminer le doute et éclairer leurs choix d'investissement.</p>
          <div className="kanea-purple-banner__actions">
            <Link to="/members/signup" className="kanea-purple-banner__cta">
              Créer un compte gratuit <ArrowRight size={16} />
            </Link>
            <Link to="/login" className="kanea-purple-banner__ghost">
              Déjà membre ? Connexion
            </Link>
          </div>
          <p className="kanea-purple-banner__reassurance">
            Sans carte bancaire · Aucun engagement pour le compte gratuit
          </p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="landing-footer">
        <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 24, textAlign: 'center', fontSize: '0.75rem', color: '#64748B', borderBottom: '1px solid var(--lp-border)', marginBottom: 24 }}>
          <strong>Avertissement légal :</strong> KaneaBourse est une plateforme d'analyse de données et d'outils d'aide à la décision. Nous ne sommes ni une SGI (Société de Gestion et d'Intermédiation) ni un Conseiller en Investissement Financier agréé par l'AMF-UMOA. Les informations, analyses, et valeurs fondamentales fournies (y compris par l'IA) le sont à titre strictement informatif et éducatif. Elles ne constituent en aucun cas une recommandation d'achat ou de vente personnalisée. Tout investissement en bourse comporte un risque de perte en capital.
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <div className="landing-footer-copy">
            © {new Date().getFullYear()} KaneaBourse — Tous droits réservés
          </div>
          <div className="landing-footer-links" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <Link to="/terms">Conditions d'utilisation</Link>
            <Link to="/confidentialite">Confidentialité</Link>
            <a href="mailto:contact@kaneabourse.com">Contact</a>
            <a href="https://www.brvm.org" target="_blank" rel="noopener noreferrer">BRVM</a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="landing-footer-social"
              aria-label="KaneaBourse sur Instagram"
            >
              <Instagram size={16} />
              Instagram
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
